import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Navigate, useNavigate, useLocation } from 'react-router-dom';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card } from './ui/card';
import { Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';
import squareLogo from '../assets/borderized_logo.png';
import { Helmet } from 'react-helmet';

const Login = () => {
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [rememberMe, setRememberMe] = useState(true);
  const location = useLocation();

  useEffect(() => {
    if (location.state?.reason === 'inactivity') {
      toast.error('Due to inactivity, you have been auto-logged out.', { duration: 5000 });
      window.history.replaceState({}, document.title);
    }
  }, [location]);

  if (user) {
    return <Navigate to="/" />;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await login(email, password, rememberMe);
      toast.success('Logged in successfully');
      navigate('/');
    } catch (error) {
      toast.error(error.message || 'Authentication failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
      <Helmet>
        <title>Login | JoldiPabo Admin</title>
      </Helmet>
      <Card className="w-full max-w-md p-8 bg-white">
        <div className="flex flex-col items-center mb-8">
          <img src={squareLogo} alt="JoldiPabo" className="w-16 h-16 object-contain mb-4" />
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
            JoldiPabo Admin
          </h1>
          <p className="text-sm text-slate-600 mt-1">
            Real Estate Management Portal
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-5">
          <div>
            <Label htmlFor="email" className="text-sm font-medium text-slate-700">Email</Label>
            <Input
              id="email"
              data-testid="login-email-input"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="mt-1.5"
              placeholder="admin@joldipabo.com"
            />
          </div>

          <div>
            <Label htmlFor="password" className="text-sm font-medium text-slate-700">Password</Label>
            <div className="relative mt-1.5">
              <Input
                id="password"
                data-testid="login-password-input"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                placeholder="••••••••"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
              >
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>

          <div className="flex items-center space-x-2">
            <input
              type="checkbox"
              id="rememberMe"
              checked={rememberMe}
              onChange={(e) => setRememberMe(e.target.checked)}
              className="w-4 h-4 text-slate-900 border-slate-300 rounded focus:ring-slate-900"
            />
            <Label htmlFor="rememberMe" className="text-sm font-medium text-slate-700 cursor-pointer">
              Remember me
            </Label>
          </div>

          <Button
            type="submit"
            data-testid="login-submit-button"
            disabled={loading}
            className="w-full bg-slate-900 hover:bg-slate-800 text-white h-11 shadow-md transition-all"
          >
            {loading ? 'Please wait...' : 'Sign In'}
          </Button>
        </form>


      </Card>
    </div>
  );
};

export default Login;
